// Wire schemas for core-api's /admin/* endpoints.
//
// These routes are gated on the platform_admin JWT claim (set by auth-svc
// when the user's email is in PLATFORM_ADMIN_EMAILS). They exist so the
// internal /admin panel in the SvelteKit app can provision organizations
// against each vertical/recipe without dropping to the CLI script.
import { z } from "zod";
import { evidenceRequirementSchema, reportSchema } from "@bitacorapro/core-engine";
import { assetGroupCreateBody } from "./asset-groups.js";
import { invitationAffiliationSchema, roleSchema } from "./auth.js";
import { brandSummarySchema } from "./brands.js";
import { invitationAssetResultSchema } from "./invitations.js";
import { resolvedRecipeSchema } from "./recipes.js";
// GET /admin/recipes — exposes the in-memory recipe registry so the FE
// can render a vertical picker on the "new org" form. We surface only
// the fields the picker needs (slug, human label, the vertical's noun
// for "organization" so the picker can say "B2C broker — Corredora").
export const adminRecipeSummarySchema = z.object({
    slug: z.string(),
    // Human-readable label for the recipe itself. Derived from the labels
    // map for now (organization + asset gives enough flavor). FE may use
    // this verbatim or layer its own copy.
    organizationLabel: z.string(),
    assetLabel: z.string(),
    rootEntity: z.enum(["asset", "group"]),
});
export const adminRecipeListResponseSchema = z.object({
    recipes: z.array(adminRecipeSummarySchema),
});
// POST /admin/orgs — create an organization with a chosen recipe slug.
// No admin user is created here; the operator follows up with an
// invitation (POST /auth/invitations with organizationId) to seed the
// first member.
export const adminOrgCreateRequestSchema = z.object({
    name: z.string().min(1).max(120),
    // Validated server-side against the recipe registry — we never trust
    // a client-supplied slug to be one we ship.
    recipeSlug: z.string().min(1).max(64),
    // Optional org-level tax RUT, only relevant for verticals that source
    // the corredor identity from session_organization (ADR-0018).
    rut: z.string().nullable().optional(),
});
const adminOrgSummarySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    recipeSlug: z.string(),
    rut: z.string().nullable(),
    active: z.boolean(),
    createdAt: z.string().datetime(),
});
export const adminOrgListResponseSchema = z.object({
    orgs: z.array(adminOrgSummarySchema),
});
export const adminOrgCreateResponseSchema = adminOrgSummarySchema;
// PATCH /admin/orgs/:id — soft toggle for organizations.active. Disabled
// orgs disappear from cartera listings and auth-svc rejects new logins
// for them. Data is preserved; a future re-enable flips the flag back.
// Use the destructive DELETE endpoint when you actually want the data
// gone.
export const adminOrgPatchRequestSchema = z
    .object({
    active: z.boolean(),
})
    .strict();
export const adminOrgPatchResponseSchema = adminOrgSummarySchema;
// DELETE /admin/orgs/:id — hard cascade delete. Mirrors the CLI script
// at apps/core-api/src/scripts/delete-organization.ts. Refuses if any
// reports.signed_at IS NOT NULL on the org (signed reports are §0.4(1)
// immutable). confirmationName must equal organizations.name verbatim
// — case-sensitive. R2 objects under org/<orgId>/* orphan by design.
//
// What gets deleted: share_links (polymorphic), the auth_users accounts
// of every member of this org (hard delete — even if a user also belongs
// to another org; cascades that user's memberships everywhere, refresh
// tokens, and invitations they sent), PLUS accounts that this org invited
// by email but that never joined any org (zero memberships) — the orphans
// a never-accepted invite or an earlier delete stranded on
// auth_users_email_unique; an invitee who is an active member of another
// org is preserved. Then auth_invitations, auth_memberships, organizations
// (Postgres cascade resolves brands, asset_groups, assets, reports →
// findings/evidence/area_captures/etc., inspection_areas → checklist_items
// + item_categories, providers → provider_categories + provider_contacts).
//
// What survives: audit_events.target_organization_id (soft ref by
// design — the trail outlives the org). Note: deleting member accounts
// is intentionally aggressive (operator-chosen) so the org's invitation
// flow can be re-tested against the same emails; the CLI dev-tool at
// apps/core-api/src/scripts/delete-organization.ts still leaves
// auth_users alone.
export const adminOrgDeleteRequestSchema = z
    .object({
    confirmationName: z.string().min(1).max(120),
})
    .strict();
// POST /admin/orgs/:id/invitations/retry-failed — re-fan invitations
// for emails the writer should have invited (derived from
// assets.attributes.jefe_email + supervisor_email) but auth_invitations
// has no row for. Useful when /admin/workbook/commit succeeded the org
// tree but partially failed the invitation HTTP fan-out (the original
// response surfaces invitationErrors; over time the operator may want
// to retry without rebuilding the org).
//
// Managers-sheet rows (Usuarios) are NOT covered — those have no asset
// row to reverse-engineer from. Use the existing "Crear invitación"
// UI on the same panel for those.
export const adminOrgInvitationsRetryFailedResponseSchema = z.object({
    organizationId: z.string().uuid(),
    attempted: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    results: z.array(z.object({
        email: z.string(),
        role: z.string(),
        ok: z.boolean(),
        acceptUrl: z.string().optional(),
        error: z.string().optional(),
    })),
});
export const adminOrgDeleteResponseSchema = z.object({
    organizationId: z.string().uuid(),
    organizationName: z.string(),
    deletedAt: z.string().datetime(),
    /** Pre-destruction inventory written into the audit_events payload.
     *  Returned for the operator's confirmation toast. */
    counts: z.object({
        reports: z.number().int().nonnegative(),
        findings: z.number().int().nonnegative(),
        evidence: z.number().int().nonnegative(),
        assets: z.number().int().nonnegative(),
        assetGroups: z.number().int().nonnegative(),
        brands: z.number().int().nonnegative(),
        inspectionAreas: z.number().int().nonnegative(),
        providers: z.number().int().nonnegative(),
        shareLinks: z.number().int().nonnegative(),
        memberships: z.number().int().nonnegative(),
        invitations: z.number().int().nonnegative(),
        /** Distinct user accounts hard-deleted — every member of this org. */
        users: z.number().int().nonnegative(),
        /** Accounts hard-deleted because the org invited their email but they
         *  never joined any org (zero memberships) — orphans a never-accepted
         *  invite or an earlier delete stranded. An invitee who is an active
         *  member of another org is NOT counted here (left untouched). */
        orphanedInvitees: z.number().int().nonnegative(),
    }),
});
// GET /admin/orgs/:id — extended detail per ADR-0033. Splits from the
// summary shape so the listing endpoint stays light and the detail can
// add wire-derived blocks without leaking to listing consumers.
//
// `configuration` is the same shape `/recipes/me` returns (resolvedRecipe
// merged with organizations.config). Wire derives from engine per §0.4(7);
// see ADR-0033 §Decision item 3.
//
// `brands` is the per-brand logo carrier per ADR-0033 §Decision item 1.
// Verticals without recipe.brands?.enabled still have exactly one default
// brand row (auto-created at org-creation per ADR-0033 §Consequence 2).
//
// `members[]` is NOT here. It travels via auth-svc on
// `GET /admin/orgs/:id/members` so per-service ownership stays structural
// (ADR-0022 pattern); the dashboard composes the two parallel queries.
//
// `configurationError` is set when parseOrganizationConfig() throws on a
// legacy/corrupt config JSONB; the operator sees the raw blob and can fix
// it via the admin script instead of getting a 500.
//
// `isDefault` (ADR-0164) marks the org's default brand — the row with
// `brands.is_default = true`, one per org. Optional so an older core-api
// response still parses.
export const adminOrgBrandSchema = brandSummarySchema.extend({
    isDefault: z.boolean().optional(),
});
export const adminOrgCountsSchema = z.object({
    assetGroups: z.number().int().nonnegative(),
    assets: z.number().int().nonnegative(),
    reports: z.number().int().nonnegative(),
    signedReports: z.number().int().nonnegative(),
});
export const adminOrgDetailResponseSchema = adminOrgSummarySchema.extend({
    configuration: resolvedRecipeSchema.nullable(),
    configurationError: z
        .object({
        code: z.literal("schema_mismatch"),
        message: z.string(),
    })
        .nullable(),
    brands: z.array(adminOrgBrandSchema),
    counts: adminOrgCountsSchema,
    // Corporate logo of the organization itself (`organizations.logo_url`,
    // ADR-0164), separate from every brand logo. Optional so an older
    // core-api response still parses.
    logoUrl: z.string().url().nullable().optional(),
    logoUpdatedAt: z.string().datetime().nullable().optional(),
});
// brands.config JSONB (ADR-0020, ADR-0033). The column is free-form on
// purpose, so this schema is `.passthrough()` with every key optional: it
// types the keys we know about without rejecting the ones other writers
// add. Writers: the dashboard logo commit (logoUrl, logoUpdatedAt,
// logoSquaredAt), repad-brand-logos (same three) and ingest-brand-logos
// (those three plus the logoSource* provenance keys). `website` is read by
// ingest-brand-logos as the domain to discover a logo from.
export const brandConfigSchema = z
    .object({
    logoUrl: z.string().url(),
    logoUpdatedAt: z.string().datetime(),
    logoSquaredAt: z.string().datetime(),
    // How the current logo was obtained, e.g. "script_discovery".
    logoSource: z.string().min(1),
    // The third-party URL the logo was downloaded from (provenance only;
    // nothing ever fetches it again).
    logoSourceUrl: z.string().min(1),
    logoSourcedAt: z.string().datetime(),
    website: z.string(),
    notes: z.string(),
})
    .partial()
    .passthrough();
// POST /admin/orgs/:id/brands/:brandId/logo/upload-url — issues a
// presigned PUT to media-svc via the internal channel. Server generates
// the objectKey (UUIDv4) so the client cannot smuggle a path-traversal or
// pin a predictable name. Whitelist of contentType is enforced here AND
// in media-svc.
export const adminBrandLogoUploadUrlRequestSchema = z
    .object({
    contentType: z.enum(["image/png", "image/jpeg", "image/webp"]),
    contentLength: z
        .number()
        .int()
        .positive()
        .max(2 * 1024 * 1024),
})
    .strict();
export const adminBrandLogoUploadUrlResponseSchema = z.object({
    uploadUrl: z.string().url(),
    objectKey: z.string(),
    publicUrl: z.string().url(),
    expiresAt: z.string().datetime(),
    requiredHeaders: z.record(z.string(), z.string()),
});
// POST /admin/orgs/:id/brands/:brandId/logo/commit — promotes a freshly
// PUT-uploaded object to the canonical brand logo. The server validates
// the objectKey against a regex bound to the URL's :id and :brandId AND
// issues a HEAD to confirm the object exists before writing the URL into
// brands.config.logoUrl. previousLogoUrl is captured in the audit event
// (not in the response) for forensics.
export const adminBrandLogoCommitRequestSchema = z
    .object({
    objectKey: z.string().min(1).max(512),
    // Which logo slot the object commits into (default "square"):
    //   square → brands.config.logoUrl   (padded client-side, isotipo)
    //   wide   → brands.config.logoWideUrl (horizontal, owner header —
    //            canal único fase 1; never padded, no logoSquaredAt)
    kind: z.enum(["square", "wide"]).optional(),
})
    .strict();
export const adminBrandLogoCommitResponseSchema = z.object({
    brand: adminOrgBrandSchema,
});
// POST /admin/orgs/:id/logo/{upload-url,commit} — the organization's own
// corporate logo (ADR-0164), the same two-step flow as the brand logo
// above. The request shapes are aliases, not copies, so the two flows
// can't drift. The server mints and accepts only keys of the form
// `org/<orgId>/org-logo/<uuidv4>.(png|jpg|jpeg|webp)` (media-svc
// keyPrefix "org-logo"); SVG is not accepted.
export const adminOrgLogoUploadUrlRequestSchema = adminBrandLogoUploadUrlRequestSchema;
export const adminOrgLogoUploadUrlResponseSchema = adminBrandLogoUploadUrlResponseSchema;
// Derived, minus `kind`: the organization has a single logo slot, so the
// brand-only square/wide selector is rejected here (.strict() is kept).
export const adminOrgLogoCommitRequestSchema = adminBrandLogoCommitRequestSchema.omit({
    kind: true,
});
export const adminOrgLogoCommitResponseSchema = z.object({
    organization: z.object({
        id: z.string().uuid(),
        logoUrl: z.string().url(),
        logoUpdatedAt: z.string().datetime(),
    }),
});
// PUT /admin/orgs/:id/default-brand — marks one brand of the org as its
// default (`brands.is_default`, ADR-0164). The response returns the whole
// brand list so the dashboard re-renders the badge without a second GET.
export const adminOrgDefaultBrandUpdateRequestSchema = z
    .object({
    brandId: z.string().uuid(),
})
    .strict();
export const adminOrgDefaultBrandUpdateResponseSchema = z.object({
    defaultBrandId: z.string().uuid(),
    brands: z.array(adminOrgBrandSchema),
});
// POST /admin/orgs/:id/brands — platform-admin manually adds a brand
// row to an existing org. Most useful for facility orgs that started
// single-brand (default brand seeded from the org name) and later
// need to split into multiple brands (e.g. FORUS adding "Cat" after
// onboarding with just "Hush Puppies"). The created brand has empty
// config — the logo upload uses the existing /logo/upload-url flow.
export const adminBrandCreateRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
})
    .strict();
export const adminBrandCreateResponseSchema = z.object({
    brand: adminOrgBrandSchema,
});
// POST /admin/asset-groups/bulk — platform-admin path to seed Zonas /
// Proyectos for an org that the operator picks. The endpoint accepts one
// `organizationId` per request and an array of rows; each row goes through
// `assetGroupCreateBody` (the same schema POST /asset-groups uses) and
// `ensureKindMatchesRecipe`. Inserts happen row by row, so the response
// carries per-row results — the UI renders a green check or a red error
// against each line of the CSV the operator uploaded. #0148.
//
// Rows arrive as `unknown` and are parsed individually inside the handler
// so that one malformed row fails alone rather than aborting the whole
// batch. `assetGroupCreateBody` is exported separately for the per-row
// parse.
export const adminAssetGroupBulkRequestSchema = z.object({
    organizationId: z.string().uuid(),
    rows: z.array(z.unknown()).min(1).max(500),
});
// Re-exported so callers (frontend, scripts) can compose the per-row body
// without re-deriving the schema. The bulk handler runs this against each
// `rows[i]`.
export { assetGroupCreateBody as adminAssetGroupBulkRowSchema };
// GET /admin/orgs/:id/asset-groups — platform-admin view of one org's
// Zonas. Used by the /admin/tiendas single-create panel to populate its
// Zona dropdown without the operator needing to know UUIDs. Same gate as
// every other /admin/* endpoint. #0151.
export const adminOrgAssetGroupSummarySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    kind: z.string().nullable(),
});
export const adminOrgAssetGroupsResponseSchema = z.object({
    groups: z.array(adminOrgAssetGroupSummarySchema),
});
// GET /admin/orgs/:id/assets — platform-admin flat list of one org's
// Tiendas (assets). Powers the multi-store picker in the "Invitar
// miembro" form when the chosen role is store_manager or
// facility_supervisor (ADR-0047, widened for store_manager without a new
// ADR — see docs/decisions/0091-facility-invite-role-widening-known-gap.md
// for the deferred cross-vertical hardening this widening intentionally
// left out). Same platform-admin gate as every other /admin/* endpoint.
// `unit` is the tienda name; `assetGroupName` is the resolved Zona for
// display. Deliberately omits supervisor_email — the picker needs to
// choose stores, not see the current holder's PII.
export const adminOrgAssetSummarySchema = z.object({
    id: z.string().uuid(),
    unit: z.string().nullable(),
    address: z.string().nullable(),
    assetGroupId: z.string().uuid().nullable(),
    assetGroupName: z.string().nullable(),
    // True when the store already has a bound supervisor (supervisor_user_id
    // set). The picker uses this to mark a row as already-covered without
    // leaking who covers it. Known gap (see ADR-0091): this does NOT reflect
    // store_manager coverage (store_manager_user_id) — a store with a bound
    // jefe but no supervisor still reads as "free" here. The server-side
    // stamp guard still catches it correctly at submit time (reported as
    // already_assigned), so the only cost is a stale "ya asignada" badge in
    // the picker preview, not a silent mis-invite.
    hasSupervisor: z.boolean(),
});
export const adminOrgAssetsResponseSchema = z.object({
    assets: z.array(adminOrgAssetSummarySchema),
});
// GET /admin/orgs/:id/reports — platform-admin per-org report roster,
// the entry point for minting a support-access grant against a specific
// report (ADR-0094 §10, ticket #0487; the core-api route is ticket
// #0494). Same platform-admin gate as every other /admin/* endpoint on
// this page — this is NOT a support-access surface itself, and no
// support-access token can ever satisfy req.requirePlatformAdmin()
// (that token carries no `role`/`org` claim, so it fails auth entirely
// before this route's own logic runs).
//
// Field set is deliberately a strict subset of, and in one respect
// narrower than, supportReportViewSchema (support.ts) — the TOTP-gated,
// mandatory-reason, single-resource, ≤900s-TTL, per-read-audited view.
// The rule (ADR-0094 Postscript 3): a surface with weaker access
// controls must never emit a field the stronger-gated surface refuses,
// and should not casually match it either. This roster has one boolean
// claim, no TOTP, no reason, org-wide reach, no TTL — so `signatureStatus`
// is a server-derived, non-reversible tri-state computed from signedAt
// and the presence of the constancia triple, never the raw
// constanciaNoFirmaRole/Motivo fields (which single out exactly one
// identifiable person within a small org and record an adverse
// behavioural attribution — "this person refused to sign"). `score`
// (an inspector-performance datum) and `checklistTemplateId` (no
// bearing on "which acta do I open") are deliberately omitted; the
// route handler must never select() the full reports row.
export const adminOrgReportSignatureStatusSchema = z.enum([
    "firmada",
    "pendiente",
    "firmada_con_constancia",
    // ADR-0098 postscript — a report closed by unilateral attestation
    // (facility_management), never by signature. Without this, the
    // derivation route falls through to "pendiente" for a closed acta.
    // A vertical-agnostic wire token, not the vertical's display word
    // ("reportada") — this roster is cross-org with no active recipe to
    // resolve a display word from.
    "cerrada",
]);
export const adminOrgReportRowSchema = reportSchema
    .pick({ id: true, folio: true, reportType: true, status: true, createdAt: true })
    .extend({ signatureStatus: adminOrgReportSignatureStatusSchema });
export const adminOrgReportsResponseSchema = z.object({
    reports: z.array(adminOrgReportRowSchema),
});
// POST /admin/orgs/:id/invitations — platform-admin single-person invite
// to an existing org (ADR-0047, widened to store_manager alongside
// facility_supervisor — see ADR-0091 for the cross-vertical hardening
// this widening deliberately deferred). Unlike the auth-svc
// createInvitation the dashboard previously called directly, this
// core-api route also stamps the role-appropriate attribute
// (attributes.supervisor_email for facility_supervisor,
// attributes.jefe_email for store_manager) on the selected stores so the
// existing ADR-0036 post-acceptance backfill binds the matching *_user_id
// column per store on acceptance. `assetIds` is honored only for those
// two roles; admin and company_manager invitations stay org-scoped and
// silently ignore assetIds if sent (not yet rejected — see ADR-0091).
export const adminOrgInvitationCreateRequestSchema = z
    .object({
    email: z.string().trim().toLowerCase().email(),
    role: roleSchema,
    displayName: z.string().trim().min(1).max(120).optional(),
    // The stores to assign a store_manager or facility_supervisor to.
    // The stamp set defines the bind set — only these stores get the
    // role-appropriate attribute written, so only these bind on
    // acceptance. Capped to bound the org-scoped UPDATE.
    assetIds: z.array(z.string().uuid()).min(1).max(500).optional(),
    // ADR-0164: the brand a brand-scoped role (brand_manager) is invited
    // to. auth-svc rejects it for any other role and requires it for a
    // brand-scoped one (invitationBrandScopeDenial in ./auth.js).
    scopeBrandId: z.string().uuid().optional(),
    // ADR-0165: the brand a supervisor belongs to (identity, not scope).
    // Optional: when absent, core-api derives it from the stores. Rejected
    // for a role without a brand of affiliation. Never sent to auth-svc.
    affiliationBrandId: invitationAffiliationSchema.shape.id.optional(),
})
    .strict();
// Per-asset outcome of the invite-time role-attribute stamp — extracted
// to invitationAssetResultSchema (./invitations.js, ADR-0116) so this
// route and the self-service POST /organizations/me/invitations share
// one tri-state enum rather than two that could drift. `stamped` = the
// role-appropriate attribute (jefe_email or supervisor_email) written
// and will bind on acceptance; `already_assigned` = the store already has
// a bound holder for that same role (skipped, never overwritten);
// `not_found` = the id is not an asset of this org (e.g. a stale or
// cross-org id). Surfacing this prevents the silent partial-success the
// IS NULL guard would otherwise produce.
export const adminOrgInvitationCreateResponseSchema = z.object({
    acceptUrl: z.string(),
    // Present only for store_manager / facility_supervisor invitations that
    // carried assetIds. Omitted for admin and company_manager invitations.
    assetResults: z.array(invitationAssetResultSchema).optional(),
});
// POST /admin/assets/bulk — platform-admin path to seed Tiendas / Assets
// for an org. Mirror of the asset-groups bulk shape so the /admin/tiendas
// UI is a near-copy of /admin/zonas. The endpoint only applies to
// rootEntity = "group" verticals (facility, postventa); rental orgs use
// /cartera/nuevo. #0151.
//
// `zoneName` is resolved server-side to an `assetGroupId` via
// `(organizationId, name)` lookup. Failed lookups are per-row errors —
// the rest of the batch keeps going.
export const adminAssetBulkRowSchema = z.object({
    // Friendly display name; stored in `attributes.alias` if provided.
    alias: z.string().trim().min(1).max(255).optional(),
    // Required address line (e.g. "Av. Las Condes 12000").
    address: z.string().trim().min(1).max(500),
    // Optional local/unit identifier.
    unit: z.string().trim().max(120).optional(),
    // Optional free-text notes.
    notes: z.string().trim().max(2000).optional(),
    // Required — operator-friendly reference to the parent Zona. The bulk
    // handler resolves this to assetGroupId via SELECT on (orgId, name).
    zoneName: z.string().trim().min(1).max(255),
    // Optional extra attributes (merged into the asset.attributes column).
    attributes: z.record(z.string(), z.unknown()).optional(),
});
export const adminAssetBulkRequestSchema = z.object({
    organizationId: z.string().uuid(),
    rows: z.array(z.unknown()).min(1).max(500),
});
export const adminAssetBulkResultSchema = z.object({
    index: z.number().int().nonnegative(),
    ok: z.boolean(),
    asset: z
        .object({
        id: z.string().uuid(),
        // Nullable per ADR-0020 Phase 6 — assets.address became nullable
        // because the canonical xlsx may omit the Dirección column.
        address: z.string().nullable(),
        assetGroupId: z.string().uuid(),
    })
        .optional(),
    error: z.string().optional(),
});
export const adminAssetBulkResponseSchema = z.object({
    results: z.array(adminAssetBulkResultSchema),
});
export const adminAssetGroupBulkResultSchema = z.object({
    index: z.number().int().nonnegative(),
    ok: z.boolean(),
    group: z
        .object({
        id: z.string().uuid(),
        name: z.string(),
        kind: z.string().nullable(),
    })
        .optional(),
    error: z.string().optional(),
});
export const adminAssetGroupBulkResponseSchema = z.object({
    results: z.array(adminAssetGroupBulkResultSchema),
});
// ── xlsx onboarding flow (ADR-0024) ─────────────────────────────────
//
// Two-endpoint preview/commit pattern over an `admin_workbook_drafts`
// row. Preview parses the multipart xlsx + persists the draft with
// SHA-256; commit echoes draftId+sha and the server re-verifies before
// calling writeClientWorkbook() and fanning out invitations to auth-svc.
// ADR-0088 §Ingest follow-up. A blocking parse failure (422) carries
// these under `error.details.issues` — the structured, per-row sibling
// of the non-blocking `warnings[]` below. Deliberately the SAME shape
// (scope/rowIndex/message, plus an optional column) so client code can
// share one renderer between "this stopped the preview" and "this is
// worth knowing but didn't."
export const adminWorkbookParseIssueSchema = z.object({
    scope: z.string(),
    rowIndex: z.number().int().nonnegative().optional(),
    column: z.string().optional(),
    message: z.string(),
});
export const adminWorkbookPreviewResponseSchema = z.object({
    draftId: z.string().uuid(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    expiresAt: z.string().datetime(),
    counts: z.object({
        brands: z.number().int().nonnegative(),
        tiendas: z.number().int().nonnegative(),
        categories: z.number().int().nonnegative(),
        items: z.number().int().nonnegative(),
        providers: z.number().int().nonnegative(),
        // Managers-sheet row count. Honest tally of that sheet, but NOT the
        // number of invitation emails — use distinctInvitationEmails for that.
        usuarios: z.number().int().nonnegative(),
        // Mantenciones-sheet row count (ADR-0083 periodic maintenance
        // catalog). Carries no email-bearing columns — irrelevant to
        // distinctInvitationEmails.
        mantenciones: z.number().int().nonnegative(),
        // Distinct emails the commit will fan out as auth-svc invitations:
        // deduped union of Managers + Tiendas jefe + Tiendas supervisor (see
        // collectInvitationEmails). This is the figure that matters against
        // the Resend free-tier 100/day cap. Persisted inside the counts JSONB
        // on admin_workbook_drafts; the commit path does not re-parse stored
        // counts through this schema, so a required field is safe.
        distinctInvitationEmails: z.number().int().nonnegative(),
        // ADR-0088 §Ingest follow-up. Items whose "Evidencia necesaria"
        // column resolved to something other than 'none' — the count that
        // matters for the pre-commit warning: committing this workbook
        // makes evidence mandatory to close a bloque for this many
        // checklist items. 0 when the column is absent or every cell blank.
        itemsWithEvidenceRequirement: z.number().int().nonnegative(),
    }),
    warnings: z.array(z.object({
        scope: z.string(),
        rowIndex: z.number().int().nonnegative().optional(),
        message: z.string(),
    })),
    // ADR-0088 §Ingest follow-up. Per-item breakdown so an admin can
    // verify the mapped evidenceRequirement before committing, rather
    // than discovering a mis-mapped row only after it starts gating a
    // supervisor's capture flow. Optional / omitted for recipes with no
    // inspection_checklist_items at all (residential_rental,
    // broker_postventa) — the vacuous "not applicable" case, not an
    // error. Shape deliberately mirrors AdminWorkbookCommitResponse
    // .checklistByBrand (brand → area) plus a per-item leaf, so preview
    // and post-commit can share one renderer.
    evidenceByBrand: z
        .array(z.object({
        brandName: z.string(),
        areas: z.array(z.object({
            sectionLabel: z.string(),
            items: z.array(z.object({
                label: z.string(),
                evidenceRequirement: evidenceRequirementSchema,
                /** ADR-0173 (D9) — the client's item code; null when the
                 *  workbook row has none (the commit will refuse it). */
                clientCode: z.string().nullable().optional(),
            })),
        })),
    }))
        .optional(),
    // ADR-0164. The brands the commit will create, in the order of the
    // Marcas sheet, with the one that will become the org's default brand
    // flagged — so the logo step can ask for one logo per brand before the
    // commit. Optional so an older core-api response still parses.
    brands: z.array(z.object({ name: z.string(), isDefault: z.boolean() })).optional(),
});
export const adminWorkbookCommitRequestSchema = z.object({
    draftId: z.string().uuid(),
    // Must match the sha256 returned by preview. Server reloads the
    // draft row and refuses if the stored payload_sha256 doesn't match
    // — anti-tampering seal that also catches operator typos when
    // copying the draftId.
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
});
export const adminWorkbookCommitResponseSchema = z.object({
    organizationId: z.string().uuid(),
    /** The org's default brand — the row flagged `brands.is_default`
     *  (ADR-0164; it used to be the oldest row, ADR-0033 §C2). Invariant:
     *  `defaultBrandId === brands.find((b) => b.isDefault).id` whenever
     *  `brands` is present. The dashboard drives the post-commit logo upload
     *  from it (POST /admin/orgs/:id/brands/:brandId/logo/{upload-url,commit},
     *  ADR-0033 §Decision 3) without a second GET /admin/orgs/:id. */
    defaultBrandId: z.string().uuid(),
    /** Every brand the commit created (ADR-0164), so the logo step can
     *  upload one logo per brand. Optional so an older core-api response
     *  still parses. */
    brands: z
        .array(brandSummarySchema.pick({ id: true, name: true }).extend({ isDefault: z.boolean() }))
        .optional(),
    brandsCreated: z.number().int().nonnegative(),
    zonesCreated: z.number().int().nonnegative(),
    storesCreated: z.number().int().nonnegative(),
    categoriesWritten: z.number().int().nonnegative(),
    /** Per-brand per-bloque checklist breakdown (ADR-0020 Phase 6 — the
     *  writer now materializes one inspection_areas row per unique
     *  Sección per brand, replacing the single "General" area). */
    checklistByBrand: z.array(z.object({
        brandName: z.string(),
        areas: z.array(z.object({
            sectionLabel: z.string(),
            itemCount: z.number().int().nonnegative(),
        })),
    })),
    /** ADR-0088 §Ingest follow-up. Items whose evidence_requirement was set
     *  to something other than the column default ('none'), counted across
     *  all brands (mirrors checklistByBrand's per-brand replication). */
    evidenceRequirementItemsCreated: z.number().int().nonnegative(),
    /** Total inspection_checklist_item_categories rows written across
     *  all brands (multi-category items fan out into N rows). */
    itemCategoryLinksCreated: z.number().int().nonnegative(),
    providersCreated: z.number().int().nonnegative(),
    providerCategoryLinksCreated: z.number().int().nonnegative(),
    /** Total provider_contacts rows written (one per raw Proveedores
     *  xlsx row — Phase 6 multi-contact model). */
    providerContactsCreated: z.number().int().nonnegative(),
    /** maintenance_items rows written (ADR-0083). One per Mantenciones
     *  sheet row — never multiplied by brand count. */
    maintenanceItemsCreated: z.number().int().nonnegative(),
    // True when the org-config update (severity overrides, finding
    // categories, etc.) landed in its own tx after the tree create.
    // False + configError when that tx failed — the org exists with
    // recipe defaults; operator should retry the config from the UI.
    configApplied: z.boolean(),
    configError: z.string().optional(),
    // The commit succeeds even if some invitations fail — the org tree
    // is the critical artifact. invitationErrors lists failures by
    // email so the operator can retry from the standard invite UI.
    invitationsQueued: z.number().int().nonnegative(),
    invitationErrors: z.array(z.object({
        email: z.string(),
        role: z.string(),
        error: z.string(),
    })),
});
//# sourceMappingURL=admin.js.map